import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { EMPTY, Observable, Subject, finalize, map, of, shareReplay, switchMap } from 'rxjs';
import { MatTabsModule } from '@angular/material/tabs';
import { DATA_SELECTOR_INJECTION_TOKEN, FileSource, SharedModule, TrackService } from '@aiwyn/shared';
import { FilePreviewService } from '../../../../services/file-preview.service';
import { AppState } from '../../../../state/app.state';
import { CancelFileUploadJob, DmsIntegrationStateSelectors } from '../../../../shared/components/upload-files';
import { CxpTasksDataStateService } from '../../services/cxp-tasks-data-state.service';
import { CxpTasksPermissionsService } from '../../services/cxp-tasks-permissions.service';
import {
  CXP_DEFAULT_PANEL_TAB,
  CXP_QUERY_PARAM_PANEL_TAB,
} from '../../models/routing-constants';
import { CRL_PANEL_NAVIGATOR, HostedCrlPanelNavigator, routedCrlPanelNavigator } from '../../services/crl-panel-navigator';
import { CxpCrlDetailSelectors } from '../../state/cxp-crl-detail-state.selectors';
import {
  LoadItemActivity,
  LoadItemDetail,
  MarkItemFinished,
  MarkItemNotApplicable,
  RemoveFileFromItem,
  RevokeItemAccess,
  SaveItemAnswer,
  UndoItemFinished,
  UndoItemNotApplicable,
} from '../../state/cxp-crl-detail.actions';
import {
  CrlActivityFile,
  CrlClientContact,
  CrlItemFile,
  CrlPanelTab,
  CrlRequestItem,
} from '../../models';
import { CrlOverlayNavComponent } from './components/crl-overlay-nav/crl-overlay-nav.component';
import { CrlOverlayTitleComponent } from './components/crl-overlay-title/crl-overlay-title.component';
import { CrlPanelQuestionnaireComponent } from './components/crl-panel-questionnaire/crl-panel-questionnaire.component';
import { CrlPanelFileUploadComponent } from './components/crl-panel-file-upload/crl-panel-file-upload.component';
import { CrlPanelItemActionsComponent } from './components/crl-panel-item-actions/crl-panel-item-actions.component';
import { CrlDocumentsTabComponent } from './components/crl-documents-tab/crl-documents-tab.component';
import { CrlDocumentsTabMapper, CrlStagingRow } from './components/crl-documents-tab/crl-documents-tab.mapper';
import { CrlPanelActivityComponent } from './components/crl-panel-activity/crl-panel-activity.component';
import { CrlPanelCommentsComponent } from './components/crl-panel-comments/crl-panel-comments.component';
import { CrlOverlayDetailsRailComponent } from './components/crl-overlay-details-rail/crl-overlay-details-rail.component';
import { CrlInviteDialogService } from './components/crl-invite-dialog/crl-invite-dialog.service';

/**
 * CRL per-item detail OVERLAY (PP-3642 redesign of PP-3753).
 *
 * The named `panel` outlet child of the redesigned list page, rendered as a wide
 * (~1200px) right-anchored drawer over a scrim. It shows the single selected
 * item (resolved by `CxpCrlDetailSelectors.getSelectedItemDisplay` from the
 * `:itemId` outlet param) plus the request-level context the rail needs
 * (`getSelectedRequestDisplay`). As a routed container it subscribes to
 * `backgroundEffects$()` so a DIRECT deep link to the overlay URL loads the
 * request detail too. There is intentionally NO `SubmitClientRequest` — the
 * submission model is per-item ("I'm Finished" / "Mark N/A").
 *
 * Per cxp-architecture this container owns orchestration: it composes signals
 * from the facade/selectors, dispatches the per-item write actions, and drives
 * navigation (close + prev/next item); the sub-components under `components/`
 * are presentational.
 */
@Component({
  selector: 'cxp-crl-panel',
  templateUrl: './cxp-crl-panel.component.html',
  styleUrls: ['./cxp-crl-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SharedModule,
    MatTabsModule,
    CrlOverlayNavComponent,
    CrlOverlayTitleComponent,
    CrlPanelQuestionnaireComponent,
    CrlPanelFileUploadComponent,
    CrlPanelItemActionsComponent,
    CrlDocumentsTabComponent,
    CrlPanelActivityComponent,
    CrlPanelCommentsComponent,
    CrlOverlayDetailsRailComponent,
  ],
  providers: [{ provide: DATA_SELECTOR_INJECTION_TOKEN, useValue: 'cxp-crl-panel' }],
})
export class CxpCrlPanelComponent {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  // A hosted page provides the navigator; on its own route there is none.
  private readonly panelNavigator =
    inject(CRL_PANEL_NAVIGATOR, { optional: true }) ?? routedCrlPanelNavigator(inject(ActivatedRoute).parent);
  private readonly dataState = inject(CxpTasksDataStateService);
  private readonly permissionsService = inject(CxpTasksPermissionsService);
  private readonly trackService = inject(TrackService);
  private readonly filePreviewService = inject(FilePreviewService);
  private readonly inviteDialogService = inject(CrlInviteDialogService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  /** Access-management affordances are hidden from a secure-link guest. */
  protected readonly canManageAccess = computed(() => this.permissionsService.permissions().canManageAccess);

  /**
   * The Comments tab is hidden from a secure-link guest entirely. Their session
   * carries the inviter's contact id, so both reading a thread and marking it
   * viewed would be recorded against the wrong person.
   */
  protected readonly canViewComments = computed(() => this.permissionsService.permissions().canViewComments);

  /**
   * Which overlay tab is showing, mirrored in the `tab` query param so it
   * survives a refresh and can be linked to. The list row's comment control
   * opens straight onto the thread rather than dropping the reader on Documents.
   */
  private readonly tabParam = toSignal(
    this.route.queryParamMap.pipe(map((params) => params.get(CXP_QUERY_PARAM_PANEL_TAB))),
    { initialValue: null },
  );

  protected readonly selectedTabIndex = computed(() => this.indexOfTab(parsePanelTab(this.tabParam())));

  /** A tab click rewrites the param, so a refresh returns to the tab last chosen. */
  protected onTabIndexChange(index: number): void {
    const tab = this.tabAtIndex(index);
    if (tab === parsePanelTab(this.tabParam())) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [CXP_QUERY_PARAM_PANEL_TAB]: tab === CXP_DEFAULT_PANEL_TAB ? null : tab },
      queryParamsHandling: 'merge',
      // Switching tabs is not a place worth returning to with Back.
      replaceUrl: true,
    });
  }

  /**
   * The Comments tab only exists when the viewer may see comments, so Activity
   * shifts down a slot without it — and a tab that is not there falls back to
   * the default rather than selecting whatever happens to occupy its index.
   */
  private indexOfTab(tab: CrlPanelTab): number {
    if (tab === 'comments') return this.canViewComments() ? 1 : 0;
    if (tab === 'activity') return this.canViewComments() ? 2 : 1;
    return 0;
  }

  private tabAtIndex(index: number): CrlPanelTab {
    if (index === 0) return 'documents';
    if (index === 1) return this.canViewComments() ? 'comments' : 'activity';
    return 'activity';
  }

  /**
   * Whether access can be changed on this item at all: the viewer is a contact
   * rather than a guest, the item is unrestricted, and it is still open — once
   * finished the access list is frozen. Per-row eligibility is the rail's job.
   */
  protected readonly canChangeAccess = computed(
    () => this.canManageAccess() && !this.isAuditView() && !this.item()?.isRestricted && !!this.itemDisplay()?.canMarkFinished,
  );

  /** The viewer is the client owner when one of their contacts holds that flag on this item. */
  protected readonly isViewerOwner = computed(() => {
    const mine = this.currentContactIds() ?? [];
    return (this.itemDisplay()?.clientContacts ?? []).some((contact) => contact.isOwner && contact.contactId != null && mine.includes(contact.contactId));
  });

  /**
   * EVERY contact id linked to the signed-in user — the rail rows that get the
   * "YOU" pill. A user can have multiple contact records and login picks the
   * first, which is often NOT the contact assigned to this request, so a single
   * id (`getLoggedInUserContactId` / `ContactUser.getContactId()`) misses the
   * right row. `AppState.getUserContactIds` is the full set, resolved on login by
   * email match — the same source the tasks/deliverables queries use.
   */
  protected readonly currentContactIds = this.store.selectSignal(AppState.getUserContactIds);

  /** The selected item, blended + with per-item action flags + prev/next pre-computed. */
  protected readonly itemDisplay = toSignal(this.store.select(CxpCrlDetailSelectors.getSelectedItemDisplay));
  /** Request-level context for the right rail (team / client access / due date). */
  protected readonly requestDisplay = toSignal(this.store.select(CxpCrlDetailSelectors.getSelectedRequestDisplay));

  protected readonly item = computed<CrlRequestItem | null>(() => this.itemDisplay()?.item ?? null);

  /**
   * Restricted-visibility audit view for the selected item — the masked case
   * where the caller sees the item exists but must not interact with it or see
   * its private content. Drives the response card, the comments tab and the
   * Invite affordance; the activity feed is masked server-side instead.
   */
  protected readonly isAuditView = computed<boolean>(() => this.itemDisplay()?.isAuditView ?? false);

  /**
   * Just the selected item's id, memoized off `itemDisplay`. The per-item load
   * effect depends on THIS (a primitive) rather than the whole `itemDisplay`
   * object: `LoadItemDetail` patches `detailData`, which makes
   * `getSelectedItemDisplay` recompute a fresh object every time — reading the
   * object directly in the effect would re-fire the load on its own result, an
   * infinite `/item/detail` + `/item/activity` fetch loop. The `computed` emits
   * only when the id value actually changes (Object.is), so the loads fire once
   * per real item navigation.
   */
  private readonly selectedItemId = computed<number | null>(() => this.itemDisplay()?.itemId ?? null);

  /**
   * Drives the Replace File flow: emits the flagged file's FILE id to the
   * file-upload component, which opens its dedicated replace picker whose
   * single-call endpoint uploads the replacement AND supersedes/versions the
   * original server-side in one request.
   */
  protected readonly replaceTrigger$ = new Subject<{ originalFileId: number }>();

  /** Reactive view of all DMS upload records (progress updates flow through here). */
  private readonly allUploadJobs = this.store.selectSignal(DmsIntegrationStateSelectors.getUploadFileJobs);

  /**
   * Staging rows for the Documents tab: the uploads bound to the SELECTED item,
   * with live progress, minus any whose real file has already landed. Built
   * entirely from global state, so they read the same whether the overlay has
   * been open since the drop or was reopened onto an upload already running.
   */
  protected readonly stagingRows = computed<CrlStagingRow[]>(() => {
    const selectedItemId = this.selectedItemId();
    if (selectedItemId === null) return [];
    return CrlDocumentsTabMapper.toStagingRows(this.allUploadJobs(), selectedItemId, this.item()?.files ?? []);
  });

  /**
   * Which status-moving call this panel has in flight. Local rather than in NGXS
   * because it spans two dispatches — an answer save can still be on the wire
   * when the client asks to finish — and because the spinner starts on the click,
   * before either call has been made.
   */
  private readonly pendingActionSignal = signal<'finish' | 'notApplicable' | null>(null);
  protected readonly pendingAction = this.pendingActionSignal.asReadonly();
  protected readonly isSubmitting = computed(() => this.pendingActionSignal() !== null);

  /**
   * The answer as the textarea currently reads it, or null until the client
   * types. The saved answer lags a blur behind, so the gate below prefers this —
   * otherwise the client types, sees the button still inert, and clicks nothing.
   */
  private readonly draftAnswer = signal<string | null>(null);

  /** The in-flight answer save, shared so a finish can queue behind it. */
  private pendingAnswerSave$: Observable<unknown> | null = null;

  /**
   * Whether the item still has nothing to submit. A questionnaire is judged on
   * the text on screen, since that is what the client is about to send; a
   * document item has no draft state, so the selector's answer stands.
   */
  protected readonly isMissingResponse = computed(() => {
    const display = this.itemDisplay();
    if (!display) return false;
    if (!display.isQuestionnaire) return display.isMissingResponse;

    const draft = this.draftAnswer();
    return (draft ?? this.item()?.answer ?? '').trim().length === 0;
  });

  /**
   * True while a file is uploading TO THE SELECTED ITEM. Gates "I'm Finished",
   * which would otherwise suggest the client can walk away and still have the
   * file arrive. Reads the same item-scoped rows the Documents tab renders, so a
   * bulk drop on the list, or an upload to another item, never counts.
   */
  protected readonly isUploadingResponse = computed(() => this.stagingRows().some((row) => row.state === 'uploading'));

  /**
   * The selected item's activity feed + loading flag, fetched per-item from the
   * dedicated endpoint (state slice, gated on the selected item so a previous
   * item's feed never leaks through).
   */
  private readonly activityView = toSignal(this.store.select(CxpCrlDetailSelectors.getSelectedItemActivity), {
    initialValue: { entries: [], isLoading: false },
  });
  protected readonly itemActivity = computed(() => this.activityView().entries);
  protected readonly itemActivityLoading = computed(() => this.activityView().isLoading);

  constructor() {
    // Direct deep link to the overlay URL: the parent list route sets the request
    // id, but the detail-load chain lives in the facade's background effects, so
    // the overlay subscribes too (sibling/child routes have no shared subscriber).
    // A hosted page drives that load itself and has no deep link to recover.
    if (!(this.panelNavigator instanceof HostedCrlPanelNavigator)) {
      this.dataState.backgroundEffects$().pipe(takeUntilDestroyed()).subscribe();
    }

    // Load the item's lazy detail (files + answer) AND its activity feed whenever
    // the selected item changes (initial open + prev/next navigation). Each state
    // handler keys its result to the item so a slower load for a since-abandoned
    // item is dropped.
    // A draft answer belongs to the item it was typed into.
    effect(() => {
      this.selectedItemId();
      this.draftAnswer.set(null);
    });

    effect(() => {
      const itemId = this.selectedItemId();
      if (itemId !== null) {
        // Always load the masked-safe item detail — the backend masks its content
        // and the panel needs it to render the (audit) item.
        this.store.dispatch(new LoadItemDetail(itemId));
        // The activity feed is masked server-side too (per-row `isMasked`, with
        // content replaced by "......"), so a restricted item is fetched like any
        // other and rendered as sent — no client-side gate.
        this.store.dispatch(new LoadItemActivity(itemId));
      }
    });
  }

  /** Jump to another item — re-points the panel at its id. */
  onNavigateToItem(item: CrlRequestItem | null): void {
    if (!item) return;
    this.panelNavigator.openItem(item.id);
  }

  /** Close → drop the selection, returning to the list-only view. */
  onClose(): void {
    this.panelNavigator.closeItem();
  }

  /** Questionnaire save-on-blur → real `ClientRequestSubmitAnswers` via the bridge. */
  onAnswerSaved(answer: string): void {
    const itemId = this.itemDisplay()?.itemId;
    if (itemId == null) return;
    this.trackService.track('CXP CRL Item Answer Saved', { itemId });
    // Kept so a finish can queue behind it: the blur that starts this save fires
    // on the way down to the click asking to finish, so the save is still in the
    // air when that finish arrives.
    this.pendingAnswerSave$ = this.store.dispatch(new SaveItemAnswer(itemId, answer)).pipe(shareReplay(1));
    this.pendingAnswerSave$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  /** Live text from the questionnaire, so the action row tracks the field rather than the last save. */
  onAnswerTyped(answer: string): void {
    this.draftAnswer.set(answer);
  }

  /**
   * "I'm Finished" → In Review. Any answer still on its way is stored first, so
   * the item is never submitted without the text the client was looking at when
   * they pressed the button — and not submitted at all if that text was refused,
   * which would leave the firm reviewing an answer the server never took. The
   * refusal has already said so in a snackbar.
   */
  onMarkFinished(): void {
    const itemId = this.itemDisplay()?.itemId;
    if (itemId == null || this.isSubmitting()) return;
    this.trackService.track('CXP CRL Item Marked Finished', { itemId });
    this.runPendingAction('finish', () =>
      this.hasUnsavedAnswer(itemId) ? EMPTY : this.store.dispatch(new MarkItemFinished(itemId)),
    );
  }

  /** Whether this item's last answer save was refused and not yet retried. */
  private hasUnsavedAnswer(itemId: number): boolean {
    return this.store.selectSnapshot(CxpCrlDetailSelectors.slices.answerSaveFailedItemIds).includes(itemId);
  }

  /**
   * Runs a status-moving call behind any answer save still in flight, holding the
   * card inert from the click until the server answers. The wait is the point:
   * the client is never told something has happened before it has.
   */
  private runPendingAction(action: 'finish' | 'notApplicable', run: () => Observable<unknown>): void {
    this.pendingActionSignal.set(action);
    (this.pendingAnswerSave$ ?? of(null))
      .pipe(
        switchMap(() => run()),
        finalize(() => this.pendingActionSignal.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /** Undo "I'm Finished" → back to open via the undo-finished endpoint (optimistic + reload). */
  onUndoFinished(): void {
    const itemId = this.itemDisplay()?.itemId;
    if (itemId == null) return;
    this.trackService.track('CXP CRL Item Finished Undone', { itemId });
    this.store.dispatch(new UndoItemFinished(itemId));
  }

  /** "Mark as Not Applicable" with optional note → real `SetClientRequestItemNotApplicable`. */
  onMarkNotApplicable(note: string | null): void {
    const itemId = this.itemDisplay()?.itemId;
    if (itemId == null || this.isSubmitting()) return;
    this.trackService.track('CXP CRL Item Marked Not Applicable', { itemId });
    this.runPendingAction('notApplicable', () => this.store.dispatch(new MarkItemNotApplicable(itemId, note)));
  }

  /** Undo "Mark as Not Applicable" → the item returns to its open state. */
  onUndoNotApplicable(): void {
    const itemId = this.itemDisplay()?.itemId;
    if (itemId == null) return;
    this.trackService.track('CXP CRL Item Not Applicable Undone', { itemId });
    this.store.dispatch(new UndoItemNotApplicable(itemId));
  }

  /**
   * Preview a file (reference or client upload) — opens the shared DMS previewer,
   * mirroring the legacy `file-item` / `request-item-card` flow. `preview()`
   * needs the numeric `id` and uses `dmsFileId` for the DMS previewer path;
   * `CrlItemFile.name` already carries the extension.
   */
  onPreviewFile(file: CrlItemFile): void {
    this.trackService.track('CXP CRL Item File Preview', { fileId: file.fileId });
    this.filePreviewService.preview({
      id: file.fileId,
      fileSource: FileSource.CLIENT_REQUEST,
      name: file.name,
      dmsFileId: file.dmsId ?? undefined,
    });
  }

  /**
   * File download — STUB. The real wire (app-local `FileApiService.downloadFile`)
   * is a domain/API call that belongs behind a thin CXP action service — not yet
   * built for the redesign. Fires analytics today.
   *
   * TODO(PP-3753 follow-up): wire download via a CXP file action service.
   */
  onDownloadFile(file: CrlItemFile): void {
    this.trackService.track('CXP CRL Item File Download', { fileId: file.fileId });
  }

  /**
   * Preview an activity file chip — the SAME real DMS previewer the Documents tab
   * uses (`FilePreviewService.preview`). The activity reference file carries the
   * numeric `id` (previewer key) and the DMS `externalId` (previewer `dmsFileId`).
   */
  onPreviewActivityFile(file: CrlActivityFile): void {
    this.trackService.track('CXP CRL Activity File Preview', { fileId: file.id });
    this.filePreviewService.preview({
      id: file.id,
      fileSource: FileSource.CLIENT_REQUEST,
      name: file.name,
      dmsFileId: file.externalId ?? undefined,
    });
  }

  /**
   * Download an activity file chip. There is no real download wire for the CRL
   * redesign yet (the Documents tab's `onDownloadFile` is an analytics-only stub,
   * TODO PP-3753), so the download icon reuses the real preview path — the only
   * working affordance — plus analytics. Swap to a proper download once a CXP
   * file-download action service exists.
   */
  onDownloadActivityFile(file: CrlActivityFile): void {
    this.trackService.track('CXP CRL Activity File Download', { fileId: file.id });
    this.filePreviewService.preview({
      id: file.id,
      fileSource: FileSource.CLIENT_REQUEST,
      name: file.name,
      dmsFileId: file.externalId ?? undefined,
    });
  }

  /**
   * Replace a flagged file: emit the flagged file's FILE id to the file-upload
   * component, which opens its dedicated replace picker whose single-call endpoint
   * uploads the replacement AND supersedes/versions the original server-side (NO
   * client-side delete of the original).
   */
  onReplaceFile(file: CrlItemFile): void {
    this.trackService.track('CXP CRL Item File Replace', { fileId: file.fileId });
    this.replaceTrigger$.next({ originalFileId: file.fileId });
  }

  /** Remove an uploaded client file (the row "X") → delete endpoint + optimistic drop. */
  onRemoveFile(file: CrlItemFile): void {
    const itemId = this.itemDisplay()?.itemId;
    if (itemId == null) return;
    this.trackService.track('CXP CRL Item File Remove', { fileId: file.fileId });
    // The backend's ClientRequestRemoveFile filters submission files by the FILE
    // id (`submissionFile.fileId in fileIds`), NOT the submission-record id — so
    // send `fileId`, not `id`.
    this.store.dispatch(new RemoveFileFromItem(itemId, file.fileId));
  }

  /** Cancel an in-flight DMS upload (the uploading row "X"). */
  onCancelUpload(row: CrlStagingRow): void {
    this.trackService.track('CXP CRL Item Upload Cancel', { jobId: row.jobId });
    this.store.dispatch(new CancelFileUploadJob(row.jobId));
  }

  /**
   * "Invite" (rail Client-access header) → open the invite dialog for the current
   * item. The dialog dispatches the invite action and the state handler reloads
   * the detail, so the panel has nothing further to do here.
   */
  onInvite(): void {
    // Defense in depth: the rail hides the Invite affordance for an audit view,
    // but never open the dialog for a masked restricted item regardless.
    if (this.isAuditView()) return;
    const itemId = this.itemDisplay()?.itemId;
    if (itemId == null) return;
    this.inviteDialogService.openInviteDialog(itemId);
  }

  /** Removes one person's access to this item. No confirm step by design. */
  onRemoveContact(contact: CrlClientContact): void {
    const itemId = this.itemDisplay()?.itemId;
    if (itemId == null || !this.canChangeAccess()) return;
    this.trackService.track('CXP CRL Item Access Removed', {
      itemId,
      kind: contact.kind,
    });
    this.store.dispatch(new RevokeItemAccess(itemId, { contactId: contact.contactId, secureLinkId: contact.secureLinkId }));
  }

  /** "View all activity" — STUB (a follow-up opens the full activity view). */
  onViewAllActivity(): void {
    this.trackService.track('CXP CRL Item View All Activity', { itemId: this.itemDisplay()?.itemId ?? 0 });
  }
}


/**
 * A `tab` query param value, or the default. Anything unrecognised — a typo, a
 * stale link, a tab that no longer exists — reads as the default rather than
 * failing.
 */
function parsePanelTab(value: string | null): CrlPanelTab {
  return value === 'comments' || value === 'activity' ? value : CXP_DEFAULT_PANEL_TAB;
}
